<template>
  <div
    v-if="pageLoading"
    class="m-3"
  >
    <placeholder-content :is-load="true" />
  </div>
  <div
    v-else-if="!hasPermission"
    class="m-3"
  >
    <access-denied/>
  </div>
  <div
    v-else
  >
    <div class="d-flex justify-content-between border-bottom mb-3">
      <ul class="nav nav-custom">
        <li class="nav-item">
          <a class="nav-link active">
            {{ $t('All') }}
            <span class="badge rounded-pill bg-primary">
              {{ datatable.total }}
            </span>
          </a>
        </li>
      </ul>
      <column-toggle
        :headers="allHeaders"
        storage-key="stock-adjustments.columns"
        v-model="visibleColumns"
      />
    </div>
    <div class="d-flex justify-content-start mx-3 flex-column flex-sm-row">
      <div class="btn-group btn-group-sm me-sm-2 mb-3">
        <button
          type="button"
          class="btn btn-sm btn-primary"
          @click="() => {
            offcanvasFroms.open = true;
            offcanvasFroms.type = 'CREATE';
          }"
        >
          <i class="fa-solid fa-circle-plus me-2" />
          {{ $t('Create') }}
        </button>
      </div>

      <div class="custom-input-group input-group input-group-sm me-sm-2 mb-3 w-auto">
        <input
          type="search"
          class="form-control"
          :placeholder="$t('search')+'...'"
          aria-label="Search"
          aria-describedby="search-addon"
          v-model.trim="params.search"
          @keyup.enter="fetchData"
        >
        <button
          class="input-group-text"
          type="button"
          @click="fetchData"
        >
          <i class="fas fa-search" />
        </button>
      </div>
      <per-page
        @per-page="setPerPage"
      />
    </div>
    <vue3-easy-data-table
      v-model:server-options="datatable.serverOptions"
      ref="dataTable"
      :headers="visibleHeaders"
      :items="datatable.items"
      :loading="datatable.loading"
      :server-items-length="datatable.total"
      :theme-color="'#515151'"
      :hide-footer="false"
      :hide-rows-per-page="true"
      :empty-message="$t('No Available Data')"
      :table-min-height="null"
      buttons-pagination
      table-class-name='customize-table'
      :body-item-class-name="bodyItemClassNameFunction"
    >
      <template #item-transaction_date="item">
        {{ item.transaction_date? formatDate(item.transaction_date): '-' }}
      </template>
      <template #item-reason="item">
        <span
          style="text-transform: capitalize"
          :class="['badge', item.reason_type == 'increase'? 'bg-primary': (item.reason_type == 'decrease'? 'bg-danger': 'bg-secondary')]"
        >
          {{ $t(item.reason) }}
        </span>
      </template>
      <template #item-status="item">
        <span
          style="text-transform: capitalize"
          :class="['badge', item.status == 'draft'? 'bg-secondary': (item.status == 'completed'? 'bg-success': 'bg-danger')]"
        >
          {{ $t(item.status) }}
        </span>
      </template>
      <template #item-created_at="item">
        {{ formatDateTime(item.created_at) }}
      </template>
      <template #item-action="item">
        <div class="icon-link custom-actions">
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              data-bs-toggle="popover"
              data-bs-placement="top"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="() => {
                formInfo.open = true;
                formInfo.id = item.id;
              }"
            >
              <i class="fa-solid far fa-eye" />
            </button>
            <template #content>
              <div>{{ $t('View') }}</div>
            </template>
          </popper>
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              data-bs-toggle="popover"
              data-bs-placement="top"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="() => {
                offcanvasFroms.open = true;
                offcanvasFroms.id = item.id;
                offcanvasFroms.type = 'EDIT';
              }"
            >
              <i class="fa-solid fa-square-pen" />
            </button>
            <template #content>
              <div>{{ $t('Edit') }}</div>
            </template>
          </popper>
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
            v-if="item.status === 'draft'"
          >
            <button
              type="button"
              data-bs-toggle="popover"
              data-bs-placement="top"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="handleConfirm(item.id)"
            >
              <i class="fa-solid fa-circle-check" />
            </button>
            <template #content>
              <div>{{ $t('Confirm') }}</div>
            </template>
          </popper>
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
            v-if="item.status !== 'cancelled'"
          >
            <button
              type="button"
              data-bs-toggle="popover"
              data-bs-placement="top"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="handleCancel(item.id)"
            >
              <i class="fa-solid fa-ban" />
            </button>
            <template #content>
              <div>{{ $t('Cancel') }}</div>
            </template>
          </popper>
        </div>
      </template>
      <template #loading>
        <i class="fa-solid fa-spinner fa-spin fa-2xl" />
      </template>
    </vue3-easy-data-table>
    <offcanvas-form
      v-if="offcanvasFroms.open"
      :type="offcanvasFroms.type"
      :id="offcanvasFroms.id"
    />
    <offcanvas-info
      v-if="formInfo.open"
      :id="formInfo.id"
    />
  </div>
</template>
<script lang="js">
import Vue3EasyDataTable from 'vue3-easy-data-table';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import PerPage from '@/plugins/PerPage.vue';
import FieldsDef from "./assets/template/datatable/FieldsDef";
import OffcanvasForm from './OffcanvasForm.vue';
import ColumnToggle from '@/components/shared/ColumnToggle.vue';
import { loadVisibleColumns } from '@/utils/columnToggle';
import { StockAdjustments } from '@/services/endpoint/stock-adjustments.service';
import moment from "moment";
import numeral from 'numeral';
import Popper from "vue3-popper";
import OffcanvasInfo from './OffcanvasInfo.vue';

export default {
  data() {
    return {
      params: {
        limit: 10,
        page: 1,
        search: ''
      },
      formInfo: {
        open: false,
        id: 0,
      },
      pageLoading: false,
      hasPermission: false,
      datatable: {
        loading: false,
        headers: FieldsDef,
        items: [],
        total: 0,
        serverOptions: {
          page: 1,
          rowsPerPage: 10
        }
      },
      offcanvasFroms: {
        open: false,
        type: 'CREATE',
        id: 0
      },
      visibleColumns: loadVisibleColumns('stock-adjustments.columns', FieldsDef(t => t))
    }
  },
  components: {
    PlaceholderContent,
    AccessDenied,
    PerPage,
    Vue3EasyDataTable,
    OffcanvasForm,
    ColumnToggle,
    Popper,
    OffcanvasInfo
  },
  computed: {
    allHeaders() {
      return this.datatable.headers(this.$t);
    },
    visibleHeaders() {
      return this.allHeaders.filter(h => !h.togglable || this.visibleColumns.includes(h.value));
    }
  },
  async mounted() {
    this.pageLoading = true;
    await this.fetchData();
    this.pageLoading = false;
  },
  methods: {
    bodyItemClassNameFunction(column, rowNumber) {
      let className = 'align-top py-1 px-2';
      const columnCenter = [];
      const columnEnd = ['created_by'];
      if (columnCenter.includes(column)) {
        className += ' text-center';
      }
      if (columnEnd.includes(column)) {
        className += ' text-end';
      }
      return className;
    },
    async fetchData() {
      this.datatable.loading = true;
      var queryString = '?' + $.param(this.params);
      await StockAdjustments.list(queryString).then(res => {
        this.datatable.items = res.data.data;
        this.datatable.total = res.data.meta.total;
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        }
      });

      this.datatable.loading = false;
      return;
    },
    setPerPage(perPage) {
      this.params.limit = perPage;
      this.$refs.dataTable.updateRowsPerPageActiveOption(perPage);
      this.fetchData();
    },
    formatDate(value) {
      return moment(value).format('YYYY-MM-DD');
    },
    formatDateTime(value) {
      return moment(value).format('YYYY-MM-DD HH:mm:ss');
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.00");
    },
    async handleConfirm(id) {
      try {
        let self = this;
        let title = this.$t("Confirm Stock Adjustment") + '?';
        let html = this.$t('This will update the actual stock balances. The document cannot be edited after confirmation. Are you sure') + '?';
        let confirmButtonText =
          '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm");
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: title,
          // text: "You will not be able to undo this action.",
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("No, Wait"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          preConfirm: async function () {
            return StockAdjustments.confirm(id)
              .then((response) => {
                self.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Confirm Success!'));
                // $(self.$refs.offcanvaInfo).offcanvas('hide');
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else if (error?.response?.data?.code === "VALIDATION") {
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
    async handleCancel(id) {
      try {
        let self = this;
        let title = this.$t("Cancel this doc") + '?';
        let html = this.$t('Do you want to cancel this doc stock adjustment? This action cannot be undone.');
        let confirmButtonText =
          '<i class="fas fa-check-circle me-1"></i>' + this.$t("Yes, Cancel Now");
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: title,
          // text: "You will not be able to undo this action.",
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("No, Keep it"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary",
            cancelButton: "btn btn-danger me-2",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          reverseButtons: true, // สลับปุ่มเอา "ยกเลิก" ไว้ซ้าย (กันมือกดผิด)
          focusCancel: true,
          preConfirm: async function () {
            return StockAdjustments.cancel(id)
              .then((response) => {
                self.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Cancel Success!'));
                // $(self.$refs.offcanvaInfo).offcanvas('hide');
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else if (error?.response?.data?.code === "VALIDATION") {
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
  },
  watch: {
    "datatable.serverOptions" (nval, oval) {
      delete this.params.sort;
      this.params.page = nval.page;
      this.params.limit = nval.rowsPerPage;
      if (nval.sortBy && nval.sortType) this.params.sort = nval.sortBy + "|" + nval.sortType;
      this.fetchData();
    }
  }
}
</script>
